import React, { useState, useEffect, useRef } from 'react';
import BotFlowCanvas from './BotFlowCanvas';
import { apiService } from '../../services/api';
import { toast } from 'react-toastify';
import {
    TrashIcon,
    DocumentDuplicateIcon,
    PowerIcon,
    CheckCircleIcon,
    StarIcon,
    PlayIcon,
    XMarkIcon,
    PaperAirplaneIcon,
    UserIcon,
    ChatBubbleLeftRightIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';

// Use the same base URL pattern as other API calls
const API_BASE_URL = window._env_ ? window._env_.API_URL : process.env.REACT_APP_API_URL;

// Chat Flow Tester Component
const ChatFlowTester = ({ isOpen, onClose, flowData }) => {
    const [messages, setMessages] = useState([]);
    const [currentInput, setCurrentInput] = useState('');
    const [currentNode, setCurrentNode] = useState(null);
    const [variables, setVariables] = useState({});
    const [isWaitingForInput, setIsWaitingForInput] = useState(false);
    const [flowEnded, setFlowEnded] = useState(false);
    const [quickReplies, setQuickReplies] = useState([]);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && flowData) {
            resetChat();
        }
    }, [isOpen, flowData]);

    const resetChat = () => {
        setMessages([]);
        setCurrentInput('');
        const initialVariables = { user_name: 'Test User' };
        setVariables(initialVariables);
        setIsWaitingForInput(false);
        setFlowEnded(false);
        setQuickReplies([]);

        // Add initial debug message
        addMessage('🔧 Chat tester initialized', 'system', 'debug');
        addMessage(`📝 Initial variables: ${JSON.stringify(initialVariables)}`, 'system', 'debug');

        // Find start node and begin flow
        const startNode = flowData.nodes?.find(node => node.type === 'start');
        if (startNode) {
            setCurrentNode(startNode);
            setTimeout(() => executeNode(startNode, initialVariables), 500);
        } else {
            addMessage('❌ No start node found in flow', 'system', 'error');
        }
    };

    const addMessage = (content, sender = 'bot', type = 'text', metadata = {}) => {
        const message = {
            id: Date.now() + Math.random(),
            content,
            sender,
            type,
            timestamp: new Date(),
            ...metadata
        };
        setMessages(prev => [...prev, message]);
        return message;
    };

    const replaceVariables = (text, currentVariables = variables) => {
        if (!text) return text;

        // Add debug logging
        console.log('Replacing variables in text:', text);
        console.log('Available variables:', currentVariables);

        const result = text.replace(/\{\{([^}]+)\}\}/g, (match, varName) => {
            const trimmedVarName = varName.trim();
            const value = currentVariables[trimmedVarName];

            console.log(`Variable ${trimmedVarName}:`, value);

            if (value !== undefined && value !== null) {
                return String(value);
            }

            // Return the original placeholder if variable not found
            return match;
        });

        console.log('Result after replacement:', result);
        return result;
    };

    const evaluateCondition = (condition, currentVariables = variables) => {
        if (!condition || !condition.field) {
            console.log('Invalid condition:', condition);
            return false;
        }

        const fieldName = condition.field.trim();
        const value = currentVariables[fieldName];
        const expectedValue = condition.value;
        const operator = condition.operator || 'equals';

        console.log(`Evaluating condition: ${fieldName} ${operator} ${expectedValue}`);
        console.log(`Variable value: "${value}" (type: ${typeof value})`);
        console.log(`Expected value: "${expectedValue}" (type: ${typeof expectedValue})`);

        // Handle undefined/null values
        if (value === undefined || value === null) {
            console.log('Variable value is undefined/null');
            return false;
        }

        const stringValue = String(value).trim();
        const stringExpected = String(expectedValue).trim();

        let result = false;

        switch (operator) {
            case 'equals':
                result = stringValue.toLowerCase() === stringExpected.toLowerCase();
                break;
            case 'contains':
                result = stringValue.toLowerCase().includes(stringExpected.toLowerCase());
                break;
            case 'startsWith':
                result = stringValue.toLowerCase().startsWith(stringExpected.toLowerCase());
                break;
            case 'endsWith':
                result = stringValue.toLowerCase().endsWith(stringExpected.toLowerCase());
                break;
            case 'regex':
                try {
                    result = new RegExp(stringExpected, 'i').test(stringValue);
                } catch (e) {
                    console.error('Invalid regex:', stringExpected, e);
                    result = false;
                }
                break;
            case 'notEquals':
                result = stringValue.toLowerCase() !== stringExpected.toLowerCase();
                break;
            case 'isEmpty':
                result = stringValue === '';
                break;
            case 'isNotEmpty':
                result = stringValue !== '';
                break;
            default:
                console.warn('Unknown operator:', operator);
                result = false;
        }

        console.log(`Condition result: ${result}`);
        return result;
    };

    const findNextNode = (currentNodeId, conditionResult = null) => {
        const edges = flowData.edges?.filter(edge => edge.source === currentNodeId) || [];

        if (edges.length === 0) {
            console.log('No outgoing edges found for node:', currentNodeId);
            return null;
        }

        // For condition nodes, find the appropriate edge based on the result
        if (conditionResult !== null) {
            console.log('Looking for condition edge with result:', conditionResult);
            console.log('Available edges:', edges);

            // First try to find edge by sourceHandle (React Flow format)
            const targetHandle = conditionResult ? 'true' : 'false';
            let edge = edges.find(e => e.sourceHandle === targetHandle);

            // If not found, try to find edge by condition.result (API format)
            if (!edge) {
                edge = edges.find(e => e.condition?.result === conditionResult);
            }

            // If still not found, try to find by label (fallback)
            if (!edge) {
                const targetLabel = conditionResult ? 'True' : 'False';
                edge = edges.find(e => e.label === targetLabel);
            }

            if (edge) {
                console.log(`Found ${conditionResult ? 'true' : 'false'} edge:`, edge);
                const nextNode = flowData.nodes?.find(node => node.id === edge.target);
                console.log('Next node:', nextNode);
                return nextNode;
            } else {
                console.log(`No ${conditionResult ? 'true' : 'false'} edge found, available edges:`, edges);
                return null;
            }
        }

        // For other nodes, take the first available edge
        const edge = edges[0];
        console.log('Taking first edge:', edge);
        const nextNode = flowData.nodes?.find(node => node.id === edge.target);
        console.log('Next node:', nextNode);
        return nextNode;
    };

    // Helper function to safely get nested object values using dot notation
    const getNestedValue = (obj, path) => {
        if (!obj || !path) return undefined;

        try {
            return path.split('.').reduce((current, key) => {
                // Handle array indices
                if (/^\d+$/.test(key)) {
                    return current?.[parseInt(key)];
                }
                return current?.[key];
            }, obj);
        } catch (error) {
            console.error('Error accessing nested value:', error);
            return undefined;
        }
    };

    // Helper function to make actual API calls
    const makeApiCall = async (config, currentVariables) => {
        const { method = 'GET', url, headers = {}, queryParams = {}, body = {}, bodyType = 'json', timeout = 10000 } = config;

        if (!url) {
            throw new Error('API URL is required');
        }

        // Replace variables in URL
        const processedUrl = replaceVariables(url, currentVariables);

        // Process headers - replace variables
        const processedHeaders = {};
        Object.entries(headers).forEach(([key, value]) => {
            processedHeaders[key] = replaceVariables(String(value), currentVariables);
        });

        // Process query parameters - replace variables
        const processedQueryParams = {};
        Object.entries(queryParams).forEach(([key, value]) => {
            processedQueryParams[key] = replaceVariables(String(value), currentVariables);
        });

        // Build URL with query parameters
        const urlObj = new URL(processedUrl);
        Object.entries(processedQueryParams).forEach(([key, value]) => {
            if (value) urlObj.searchParams.append(key, value);
        });

        // Prepare request options
        const requestOptions = {
            method: method.toUpperCase(),
            headers: {
                'Content-Type': bodyType === 'json' ? 'application/json' : 'application/x-www-form-urlencoded',
                ...processedHeaders
            }
        };

        // Add body for POST, PUT, PATCH requests
        if (['POST', 'PUT', 'PATCH'].includes(method.toUpperCase()) && body) {
            if (bodyType === 'json') {
                // Process body - replace variables in each value
                const processedBody = {};
                Object.entries(body).forEach(([key, value]) => {
                    processedBody[key] = replaceVariables(String(value), currentVariables);
                });
                requestOptions.body = JSON.stringify(processedBody);
            } else {
                // Form data
                const formData = new URLSearchParams();
                Object.entries(body).forEach(([key, value]) => {
                    const processedValue = replaceVariables(String(value), currentVariables);
                    formData.append(key, processedValue);
                });
                requestOptions.body = formData.toString();
            }
        }

        // Create timeout promise
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error(`Request timeout after ${timeout}ms`)), timeout);
        });

        // Make the actual API call
        const fetchPromise = fetch(urlObj.toString(), requestOptions);

        return Promise.race([fetchPromise, timeoutPromise]);
    };

    const executeAction = async (actionData, currentVariables) => {
        if (!actionData) return currentVariables;

        const { type, config } = actionData;
        let updatedVariables = { ...currentVariables };

        switch (type) {
            case 'setVariable':
                if (config.variable && config.value !== undefined) {
                    const variableName = config.variable.trim();
                    const rawValue = config.value;
                    const processedValue = replaceVariables(String(rawValue), currentVariables);

                    updatedVariables[variableName] = processedValue;

                    addMessage(`✅ Set variable "${variableName}" = "${processedValue}"`, 'system', 'debug');
                    console.log(`Set variable ${variableName}:`, processedValue);
                }
                break;

            case 'apiCall':
                try {
                    const apiName = config.name || 'Unknown API';
                    const method = config.method || 'GET';
                    const url = config.url || 'No URL';

                    addMessage(`🔄 Making API call: ${method} ${url}`, 'system', 'info');

                    // Make the actual API call
                    const response = await makeApiCall(config, currentVariables);

                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }

                    // Parse response
                    const responseData = await response.json();
                    console.log('API Response:', responseData);

                    addMessage(`✅ API call successful (${response.status})`, 'system', 'info');

                    // Set success variable
                    if (config.successVariable) {
                        const successVar = config.successVariable.trim();
                        updatedVariables[successVar] = 'true';
                        addMessage(`✅ Set success variable "${successVar}" = "true"`, 'system', 'debug');
                    }

                    // Process response mapping
                    if (config.responseMapping?.data) {
                        Object.entries(config.responseMapping.data).forEach(([varName, path]) => {
                            const trimmedVarName = varName.trim();
                            const value = getNestedValue(responseData, path);

                            if (value !== undefined) {
                                updatedVariables[trimmedVarName] = String(value);
                                addMessage(`📝 Mapped "${path}" → "${trimmedVarName}" = "${value}"`, 'system', 'debug');
                            } else {
                                addMessage(`⚠️ Could not find value at path "${path}"`, 'system', 'debug');
                            }
                        });
                    }

                    addMessage(`✅ API call "${apiName}" completed successfully`, 'system', 'info');

                } catch (error) {
                    console.error('API call failed:', error);
                    addMessage(`❌ API call failed: ${error.message}`, 'system', 'error');

                    // Set error variable
                    if (config.errorVariable) {
                        const errorVar = config.errorVariable.trim();
                        updatedVariables[errorVar] = error.message;
                        addMessage(`❌ Set error variable "${errorVar}" = "${error.message}"`, 'system', 'debug');
                    }

                    // Process error response mapping if available
                    if (config.errorMapping) {
                        Object.entries(config.errorMapping).forEach(([varName, value]) => {
                            const trimmedVarName = varName.trim();
                            updatedVariables[trimmedVarName] = String(value);
                            addMessage(`📝 Set error mapping "${trimmedVarName}" = "${value}"`, 'system', 'debug');
                        });
                    }
                }
                break;

            case 'database':
                addMessage(`💾 Database operation executed (simulated)`, 'system', 'info');
                break;

            default:
                addMessage(`⚡ Executed action: ${type}`, 'system', 'info');
        }

        return updatedVariables;
    };

    const executeNode = async (node, currentVariables = null) => {
        if (!node) {
            addMessage('🏁 Flow ended - no next node found', 'system', 'info');
            setFlowEnded(true);
            return;
        }

        console.log('Executing node:', node);
        setCurrentNode(node);

        // Use passed variables or current state
        const varsToUse = currentVariables || variables;

        switch (node.type) {
            case 'start':
                addMessage('🚀 Flow started', 'system', 'info');
                // Move to next node
                setTimeout(() => {
                    const nextNode = findNextNode(node.id);
                    executeNode(nextNode, varsToUse);
                }, 500);
                break;

            case 'message':
                const messageText = replaceVariables(node.data.message, varsToUse);
                if (messageText) {
                    addMessage(messageText, 'bot');
                }

                // Handle media if present
                if (node.data.media && node.data.media.enabled && node.data.media.url) {
                    const mediaUrl = replaceVariables(node.data.media.url, varsToUse);
                    const mediaType = node.data.media.type || 'file'; // Default to 'file' if type is null
                    const mediaCaption = node.data.media.caption ? replaceVariables(node.data.media.caption, varsToUse) : '';

                    addMessage(
                        `📎 ${mediaType.toUpperCase()}: ${mediaUrl}${mediaCaption ? `\n${mediaCaption}` : ''}`,
                        'bot',
                        'media'
                    );
                }

                // Move to next node
                setTimeout(() => {
                    const nextNode = findNextNode(node.id);
                    executeNode(nextNode, varsToUse);
                }, 1000);
                break;

            case 'question':
                const questionText = replaceVariables(node.data.message, varsToUse);
                if (questionText) {
                    addMessage(questionText, 'bot');
                }

                // Set up quick replies if available
                if (node.data.quickReplies && node.data.quickReplies.length > 0) {
                    const processedReplies = node.data.quickReplies
                        .filter(reply => reply && reply.trim())
                        .map(reply => replaceVariables(reply, varsToUse));
                    setQuickReplies(processedReplies);
                }

                setIsWaitingForInput(true);
                addMessage(`⏳ Waiting for user input${node.data.variable ? ` (will store in: ${node.data.variable})` : ''}`, 'system', 'debug');
                break;

            case 'condition':
                // Use the passed variables for condition evaluation
                console.log('Evaluating condition with variables:', varsToUse);
                const conditionResult = evaluateCondition(node.data.condition, varsToUse);

                const conditionDisplay = node.data.condition ?
                    `"${node.data.condition.field}" ${node.data.condition.operator} "${node.data.condition.value}"` :
                    'undefined condition';

                addMessage(
                    `🔀 Condition ${conditionDisplay}: ${conditionResult ? 'TRUE' : 'FALSE'}`,
                    'system',
                    'info'
                );

                // Move to appropriate next node
                setTimeout(() => {
                    const nextNode = findNextNode(node.id, conditionResult);
                    executeNode(nextNode, varsToUse);
                }, 1000);
                break;

            case 'action':
                const updatedVars = await executeAction(node.data.action, varsToUse);

                // Update variables state
                setVariables(updatedVars);

                // Move to next node with updated variables
                setTimeout(() => {
                    const nextNode = findNextNode(node.id);
                    executeNode(nextNode, updatedVars);
                }, 1000);
                break;

            case 'handoff':
                const handoffMessage = replaceVariables(node.data.message, varsToUse);
                if (handoffMessage) {
                    addMessage(handoffMessage, 'bot');
                }

                const reason = node.data.handoffReason ? ` (Reason: ${node.data.handoffReason})` : '';
                addMessage(`👤 Transferring to human agent${reason}`, 'system', 'info');
                setFlowEnded(true);
                break;

            case 'end':
                addMessage('🏁 Flow completed successfully', 'system', 'info');
                setFlowEnded(true);
                break;

            default:
                addMessage(`❓ Unknown node type: ${node.type}`, 'system', 'error');
                setFlowEnded(true);
        }
    };

    const handleSendMessage = (messageText = currentInput) => {
        if (!messageText.trim() || !isWaitingForInput) return;

        // Add user message
        addMessage(messageText, 'user');

        // Store variable if this is a question node
        let updatedVariables = { ...variables };

        if (currentNode?.type === 'question' && currentNode.data.variable) {
            const variableName = currentNode.data.variable.trim();
            updatedVariables[variableName] = messageText;

            console.log('Updated variables after user input:', updatedVariables);
            addMessage(`📝 Stored "${messageText}" in variable "${variableName}"`, 'system', 'debug');
        }

        // Update variables state
        setVariables(updatedVariables);

        // Clear input and quick replies
        setCurrentInput('');
        setQuickReplies([]);
        setIsWaitingForInput(false);

        // Move to next node after state has been updated - pass the updated variables
        setTimeout(() => {
            const nextNode = findNextNode(currentNode.id);
            executeNode(nextNode, updatedVariables); // Pass the updated variables
        }, 500);
    };

    const handleQuickReply = (reply) => {
        handleSendMessage(reply);
    };

    const formatTimestamp = (timestamp) => {
        return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-96 h-[600px] flex flex-col shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-blue-50 rounded-t-lg">
                    <div className="flex items-center gap-2">
                        <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600" />
                        <h3 className="font-semibold text-gray-900">Flow Tester</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={resetChat}
                            className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                            title="Reset Chat"
                        >
                            <ArrowPathIcon className="h-4 w-4" />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Variables Display */}
                <div className="p-2 bg-gray-50 border-b text-xs max-h-20 overflow-y-auto">
                    <div className="flex flex-wrap gap-1">
                        {Object.entries(variables).map(([key, value]) => (
                            <span key={key} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                {key}: {String(value)}
                            </span>
                        ))}
                        {Object.keys(variables).length === 0 && (
                            <span className="text-gray-500 italic">No variables set</span>
                        )}
                    </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.map((message) => (
                        <div
                            key={message.id}
                            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'
                                }`}
                        >
                            <div
                                className={`max-w-[80%] rounded-lg p-3 ${message.sender === 'user'
                                    ? 'bg-blue-500 text-white'
                                    : message.sender === 'system'
                                        ? message.type === 'error'
                                            ? 'bg-red-100 text-red-800 border border-red-200'
                                            : message.type === 'debug'
                                                ? 'bg-gray-100 text-gray-600 border border-gray-200 text-xs'
                                                : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                        : 'bg-gray-200 text-gray-900'
                                    }`}
                            >
                                <div className="flex items-start gap-2">
                                    {message.sender === 'bot' && <ChatBubbleLeftRightIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                                    {message.sender === 'user' && <UserIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                                    <div className="flex-1">
                                        <div className="whitespace-pre-wrap break-words">
                                            {message.content}
                                        </div>
                                        <div className={`text-xs mt-1 opacity-70`}>
                                            {formatTimestamp(message.timestamp)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Quick Replies */}
                    {quickReplies.length > 0 && isWaitingForInput && (
                        <div className="flex flex-wrap gap-2 mt-2">
                            {quickReplies.map((reply, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleQuickReply(reply)}
                                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm hover:bg-blue-200 transition-colors"
                                >
                                    {reply}
                                </button>
                            ))}
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 border-t bg-white rounded-b-lg">
                    {flowEnded ? (
                        <div className="text-center">
                            <p className="text-gray-600 text-sm mb-2">Flow completed</p>
                            <button
                                onClick={resetChat}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                            >
                                Start Over
                            </button>
                        </div>
                    ) : (
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={currentInput}
                                onChange={(e) => setCurrentInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                placeholder={
                                    isWaitingForInput
                                        ? "Type your response..."
                                        : "Waiting for bot..."
                                }
                                disabled={!isWaitingForInput}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                            <button
                                onClick={() => handleSendMessage()}
                                disabled={!isWaitingForInput || !currentInput.trim()}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                                <PaperAirplaneIcon className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const BotFlowManagement = () => {
    const [flows, setFlows] = useState([]);
    const [selectedFlow, setSelectedFlow] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showChatTester, setShowChatTester] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [flowToDelete, setFlowToDelete] = useState(null);

    // API functions
    const fetchFlows = async (params = {}) => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams();
            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);
            if (params.search) queryParams.append('search', params.search);
            if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);

            const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
            const response = await fetch(`${API_BASE_URL}/api/bot-flows${queryString}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Error fetching flows:', errorData);
                return false;
            }

            const data = await response.json();
            if (data.success) {
                // Get flows array from response data
                const flowsData = data.data.flows || data.data || [];

                // Map each flow through the preparation function
                const preparedFlows = flowsData.map(flow => ({
                    ...flow,
                    // We don't need to fully prepare nodes/edges for the list view
                    // but we ensure the basic structure is correct
                    nodes: Array.isArray(flow.nodes) ? flow.nodes : [],
                    edges: Array.isArray(flow.edges) ? flow.edges : []
                }));

                setFlows(preparedFlows);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error fetching flows:', error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const getFlowById = async (flowId) => {
        try {
            setLoading(true);
            console.log(`Fetching flow data for ID: ${flowId}`);

            const response = await fetch(`${API_BASE_URL}/api/bot-flows/${flowId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error(`Error fetching flow ${flowId}:`, errorData);
                return null;
            }

            const data = await response.json();
            console.log(`Received flow data for ID ${flowId}:`, data);

            if (data.success) {
                // Validate received data has the expected structure
                const flowData = data.data;

                if (!flowData) {
                    console.error('API returned success but no flow data');
                    return null;
                }

                if (!flowData.nodes || !flowData.edges) {
                    console.error('API returned flow data but missing nodes or edges:', flowData);
                }

                return prepareFlowForEditing(flowData);
            }

            console.error('API did not return success status:', data);
            return null;
        } catch (error) {
            console.error(`Error fetching flow ${flowId}:`, error);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const saveFlow = async (flowData, isNew = false) => {
        try {
            const url = isNew ? `${API_BASE_URL}/api/bot-flows` : `${API_BASE_URL}/api/bot-flows/${flowData._id}`;
            const method = isNew ? 'POST' : 'PUT';

            // Format nodes and edges according to the API schema
            const formattedNodes = flowData.nodes.map(node => ({
                id: node.id,
                type: node.type,
                position: node.position,
                data: {
                    label: node.data.label || '',
                    // Add type-specific properties
                    ...(node.type === 'message' && {
                        message: node.data.message || '',
                        ...(node.data.media && node.data.media.enabled && {
                            media: {
                                ...node.data.media,
                                enabled: true
                            }
                        })
                    }),
                    ...(node.type === 'question' && {
                        message: node.data.message || '',
                        variable: node.data.variable || '',
                        quickReplies: Array.isArray(node.data.quickReplies) ? node.data.quickReplies : []
                    }),
                    ...(node.type === 'condition' && {
                        condition: node.data.condition || {
                            field: '',
                            operator: 'equals',
                            value: ''
                        }
                    }),
                    ...(node.type === 'action' && {
                        action: {
                            type: node.data.action?.type || 'setVariable',
                            config: {
                                // Handle setVariable type
                                ...(node.data.action?.type === 'setVariable' && {
                                    variable: node.data.action?.config?.variable || '',
                                    value: node.data.action?.config?.value || ''
                                }),
                                // Handle apiCall type
                                ...(node.data.action?.type === 'apiCall' && {
                                    name: node.data.action?.config?.name || '',
                                    method: node.data.action?.config?.method || 'GET',
                                    url: node.data.action?.config?.url || '',
                                    headers: node.data.action?.config?.headers || {},
                                    queryParams: node.data.action?.config?.queryParams || {},
                                    bodyType: node.data.action?.config?.bodyType || 'json',
                                    body: node.data.action?.config?.body || {},
                                    responseMapping: node.data.action?.config?.responseMapping || { data: {} },
                                    errorMapping: node.data.action?.config?.errorMapping || {},
                                    successVariable: node.data.action?.config?.successVariable || 'api_success',
                                    errorVariable: node.data.action?.config?.errorVariable || 'api_error',
                                    timeout: node.data.action?.config?.timeout || 10000
                                }),
                                // Default empty config for other action types
                                ...((!node.data.action?.type || (node.data.action?.type !== 'setVariable' && node.data.action?.type !== 'apiCall')) && {})
                            }
                        }
                    }),
                    ...(node.type === 'handoff' && {
                        message: node.data.message || '',
                        handoffReason: node.data.handoffReason || ''
                    })
                }
            }));

            const formattedEdges = flowData.edges.map(edge => ({
                id: edge.id,
                source: edge.source,
                target: edge.target,
                sourceHandle: edge.sourceHandle || null,
                targetHandle: edge.targetHandle || null,
                label: edge.label || '',
                // Include condition for condition node connections
                ...(edge.condition && { condition: edge.condition })
            }));

            // Complete payload with all required fields
            const payload = {
                name: flowData.name || 'Untitled Flow',
                description: flowData.description || '',
                nodes: formattedNodes,
                edges: formattedEdges,
                variables: Array.isArray(flowData.variables) ? flowData.variables.map(v => ({
                    name: v.name || '',
                    type: v.type || 'text',
                    defaultValue: v.defaultValue || '',
                    description: v.description || ''
                })) : [],
                settings: {
                    timeout: flowData.settings?.timeout || 300000,
                    maxRetries: flowData.settings?.maxRetries || 3,
                    fallbackMessage: flowData.settings?.fallbackMessage || "I didn't understand that. Please try again."
                },
                isActive: flowData.isActive || false,
                isDefault: flowData.isDefault || false,
                version: flowData.version || 1
            };

            // Log the payload for debugging
            console.log(`${method} flow data:`, JSON.stringify(payload, null, 2));

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Error saving flow:', errorData);
                return false;
            }

            const data = await response.json();
            if (data.success) {
                await fetchFlows();
                if (isNew && data.data) {
                    setSelectedFlow(data.data);
                }
                return true;
            } else {
                console.error('Error saving flow:', data.errors || data.error || 'Unknown error');
                return false;
            }
        } catch (error) {
            console.error('Error saving flow:', error);
            return false;
        }
    };

    const deleteFlow = async (flowId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/bot-flows/${flowId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Error deleting flow:', errorData);
                return false;
            }

            await fetchFlows();
            if (selectedFlow?._id === flowId) {
                setSelectedFlow(null);
                setIsEditing(false);
            }
            setFlowToDelete(null);
            setShowDeleteModal(false);
            return true;
        } catch (error) {
            console.error('Error deleting flow:', error);
            return false;
        }
    };

    const confirmDeleteFlow = (flowId) => {
        setFlowToDelete(flowId);
        setShowDeleteModal(true);
    };

    const duplicateFlow = async (flowId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/bot-flows/${flowId}/duplicate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Error duplicating flow:', errorData);
                return false;
            }

            const data = await response.json();
            if (data.success) {
                await fetchFlows();
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error duplicating flow:', error);
            return false;
        }
    };

    const toggleFlowStatus = async (flowId, isActive) => {
        try {
            const endpoint = isActive ? 'activate' : 'deactivate';
            const response = await fetch(`${API_BASE_URL}/api/bot-flows/${flowId}/${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error(`Error ${isActive ? 'activating' : 'deactivating'} flow:`, errorData);
                return false;
            }

            await fetchFlows();
            return true;
        } catch (error) {
            console.error(`Error ${isActive ? 'activating' : 'deactivating'} flow:`, error);
            return false;
        }
    };

    const setDefaultFlow = async (flowId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/bot-flows/${flowId}/set-default`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Error setting default flow:', errorData);
                return false;
            }

            await fetchFlows();
            return true;
        } catch (error) {
            console.error('Error setting default flow:', error);
            return false;
        }
    };

    useEffect(() => {
        fetchFlows();
    }, []);

    // New flow template
    const createNewFlow = () => {
        const newFlow = {
            name: 'New Flow',
            description: 'A new conversation flow',
            isActive: false,
            isDefault: false,
            nodes: [
                {
                    id: 'start-1',
                    type: 'start',
                    position: { x: 250, y: 50 },
                    data: { label: 'Start' },
                },
                {
                    id: 'message-1',
                    type: 'message',
                    position: { x: 250, y: 150 },
                    data: {
                        label: 'Welcome Message',
                        message: 'Welcome to our service! How can I assist you today?'
                    },
                },
                {
                    id: 'message-2',
                    type: 'message',
                    position: { x: 250, y: 250 },
                    data: {
                        label: 'Media Example',
                        message: 'Here\'s some information about our services:',
                        media: {
                            enabled: true,
                            type: 'image',
                            url: 'https://example.com/images/services.jpg',
                            caption: 'Our services overview'
                        }
                    },
                },
                {
                    id: 'api-call-1',
                    type: 'action',
                    position: { x: 250, y: 350 },
                    data: {
                        label: 'API Call Example',
                        action: {
                            type: 'apiCall',
                            config: {
                                name: 'getWeather',
                                method: 'GET',
                                url: 'https://api.example.com/weather',
                                queryParams: {
                                    "city": "London",
                                    "units": "metric"
                                },
                                responseMapping: {
                                    data: {
                                        "temperature": "main.temp",
                                        "description": "weather.0.description"
                                    }
                                },
                                successVariable: "api_success",
                                errorVariable: "api_error",
                                timeout: 10000
                            }
                        }
                    },
                },
                {
                    id: 'end-1',
                    type: 'end',
                    position: { x: 250, y: 450 },
                    data: { label: 'End' },
                },
            ],
            edges: [
                {
                    id: 'e1-2',
                    source: 'start-1',
                    target: 'message-1',
                    label: 'start',
                    sourceHandle: null,
                    targetHandle: null,
                },
                {
                    id: 'e2-3',
                    source: 'message-1',
                    target: 'message-2',
                    label: 'next',
                    sourceHandle: null,
                    targetHandle: null,
                },
                {
                    id: 'e3-4',
                    source: 'message-2',
                    target: 'api-call-1',
                    label: 'next',
                    sourceHandle: null,
                    targetHandle: null,
                },
                {
                    id: 'e4-5',
                    source: 'api-call-1',
                    target: 'end-1',
                    label: 'end',
                    sourceHandle: null,
                    targetHandle: null,
                },
            ],
            variables: [
                {
                    name: 'user_name',
                    type: 'text',
                    defaultValue: 'User',
                    description: 'The user\'s name'
                }
            ],
            settings: {
                timeout: 300000, // 5 minutes
                maxRetries: 3,
                fallbackMessage: "I didn't understand that. Please try again."
            },
            version: 1
        };

        // Ensure the new flow has the correct structure for the React Flow canvas
        const preparedFlow = prepareFlowForEditing(newFlow);
        setSelectedFlow(preparedFlow);
        setIsEditing(true);
    };

    // Prepare flow data for editing by ensuring it has the correct structure for React Flow
    const prepareFlowForEditing = (flow) => {
        if (!flow) return null;

        // Create a deep copy to avoid modifying the original
        const preparedFlow = JSON.parse(JSON.stringify(flow));

        // Preserve UI state if it exists
        preparedFlow._uiState = flow._uiState || {};

        // Ensure nodes array exists and has properly formatted position data
        if (!Array.isArray(preparedFlow.nodes)) {
            preparedFlow.nodes = [];
        } else {
            preparedFlow.nodes = preparedFlow.nodes.map(node => ({
                ...node,
                // Ensure position is properly formatted as expected by React Flow
                position: {
                    x: typeof node.position === 'object' ? (node.position.x || 0) : 0,
                    y: typeof node.position === 'object' ? (node.position.y || 0) : 0
                },
                // Ensure data object exists
                data: {
                    ...(node.data || {}),
                    label: node.data?.label || node.type.charAt(0).toUpperCase() + node.type.slice(1)
                }
            }));
        }

        // Ensure edges array exists with required properties
        if (!Array.isArray(preparedFlow.edges)) {
            preparedFlow.edges = [];
        } else {
            preparedFlow.edges = preparedFlow.edges.map(edge => ({
                ...edge,
                // Ensure required edge properties exist
                id: edge.id || `e${edge.source}-${edge.target}`,
                sourceHandle: edge.sourceHandle || null,
                targetHandle: edge.targetHandle || null,
                label: edge.label || ''
            }));
        }

        console.log('Prepared flow for editing:', preparedFlow);
        return preparedFlow;
    };

    // Handle selecting a flow for viewing or editing
    const handleSelectFlow = async (flow) => {
        if (!flow || !flow._id) return;

        setLoading(true);

        try {
            // Fetch the complete flow data from the API
            const completeFlowData = await getFlowById(flow._id);

            if (completeFlowData) {
                // Already prepared by getFlowById
                setSelectedFlow(completeFlowData);
                setIsEditing(false);
            } else {
                // If API fetch fails, try to use the summary data
                console.warn('Could not fetch complete flow data, using summary data');
                const preparedFlow = prepareFlowForEditing(flow);
                setSelectedFlow(preparedFlow);
                setIsEditing(false);
            }
        } catch (error) {
            console.error('Error fetching flow details:', error);
            toast.error('Error loading flow details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveFlow = async (flowData, options = {}) => {
        // Extract options
        const { maintainFullscreen } = options;

        // Ensure we have a complete flow object with all required fields
        const updatedFlow = {
            // Preserve ID and other metadata if it exists
            ...(selectedFlow || {}),

            // Basic flow information
            name: selectedFlow?.name || flowData.name || 'Untitled Flow',
            description: selectedFlow?.description || flowData.description || '',

            // Flow content from the canvas
            nodes: Array.isArray(flowData.nodes) ? flowData.nodes : [],
            edges: Array.isArray(flowData.edges) ? flowData.edges : [],

            // Other properties - use from current flow or defaults
            variables: Array.isArray(flowData.variables)
                ? flowData.variables
                : (selectedFlow?.variables || [{
                    name: 'user_name',
                    type: 'text',
                    defaultValue: 'User',
                    description: 'The user\'s name'
                }]),

            settings: flowData.settings || selectedFlow?.settings || {
                timeout: 300000,
                maxRetries: 3,
                fallbackMessage: "I didn't understand that. Please try again."
            },

            // Status flags
            isActive: selectedFlow?.isActive || false,
            isDefault: selectedFlow?.isDefault || false,

            // Increment version
            version: (selectedFlow?.version || 0) + 1
        };

        // For debugging
        console.log('Saving updated flow:', updatedFlow, 'Options:', options);

        const isNew = !selectedFlow?._id;
        const success = await saveFlow(updatedFlow, isNew);

        if (success) {
            // Show success toast instead of alert
            toast.success('Flow saved successfully!', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });

            // Update the selectedFlow with the latest data but keep editing mode
            if (isNew) {
                // For a new flow, we need to find it in the updated flows list
                await fetchFlows();
                // Find the newly created flow (typically the most recent one)
                const latestFlows = [...flows]; // Use the already updated flows from the saveFlow function
                if (latestFlows && latestFlows.length > 0) {
                    // Find a flow with the same name we just created
                    const newFlow = latestFlows.find(f => f.name === updatedFlow.name);
                    if (newFlow && newFlow._id) {
                        console.log('Found newly created flow:', newFlow);
                        // Now fetch the complete flow data with this ID
                        const completeFlow = await getFlowById(newFlow._id);
                        if (completeFlow) {
                            // Store the fullscreen state in the flow data as a UI state property
                            completeFlow._uiState = { isFullscreen: maintainFullscreen };
                            setSelectedFlow(completeFlow);
                        }
                    }
                }
            } else if (selectedFlow?._id) {
                // For existing flow, fetch the latest data
                const updatedFlowData = await getFlowById(selectedFlow._id);
                if (updatedFlowData) {
                    // Preserve UI state from options or existing state
                    updatedFlowData._uiState = {
                        isFullscreen: maintainFullscreen !== undefined ? maintainFullscreen : selectedFlow._uiState?.isFullscreen
                    };
                    setSelectedFlow(updatedFlowData);
                }
            }

            // Keep the flow builder open - don't set setIsEditing(false)
        } else {
            // Show error toast instead of alert
            toast.error('Error saving flow. Please check console for details.', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    };

    const handleTestFlow = () => {
        if (!selectedFlow) {
            toast.warning('Please select a flow to test.');
            return;
        }
        setShowChatTester(true);
    };

    // Function to prepare flow for editing mode
    const handleEditFlow = async () => {
        if (!selectedFlow || !selectedFlow._id) return;

        setLoading(true);

        try {
            // Fetch the latest flow data to ensure we have complete information
            const completeFlowData = await getFlowById(selectedFlow._id);

            if (completeFlowData) {
                // Flow data is already prepared by getFlowById
                setSelectedFlow(completeFlowData);
                setIsEditing(true);
            } else {
                // If API fetch fails, try to use existing data
                console.warn('Could not fetch complete flow data, using existing data');
                const preparedFlow = prepareFlowForEditing(selectedFlow);
                setSelectedFlow(preparedFlow);
                setIsEditing(true);
            }
        } catch (error) {
            console.error('Error fetching flow details for editing:', error);
            toast.error('Error loading flow details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-lg">Loading flows...</div>
            </div>
        );
    }

    return (
        <div className="h-screen flex flex-col">
            <div className="flex flex-1 overflow-hidden">
                <div className="w-80 bg-gray-50 border-r h-full flex flex-col">
                    {/* Fixed header */}
                    <div className="p-4 border-b bg-gray-50">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl font-bold">Bot Flows</h2>
                            <button
                                onClick={createNewFlow}
                                className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
                            >
                                New Flow
                            </button>
                        </div>
                    </div>

                    {/* Scrollable content */}
                    <div className="flex-1 overflow-y-auto p-4">
                        <div className="space-y-2">
                            {flows.map((flow) => (
                                <div
                                    key={flow._id}
                                    className={`p-3 rounded-lg border cursor-pointer hover:bg-white transition-colors ${selectedFlow?._id === flow._id ? 'bg-blue-50 border-blue-300' : 'bg-white'
                                        }`}
                                    onClick={() => {
                                        handleSelectFlow(flow);
                                    }}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-medium text-sm">{flow.name}</h3>
                                        <div className="flex items-center space-x-1">
                                            {flow.isDefault && (
                                                <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                                                    Default
                                                </span>
                                            )}
                                            <span
                                                className={`px-2 py-1 text-xs rounded ${flow.isActive
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-gray-100 text-gray-700'
                                                    }`}
                                            >
                                                {flow.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                    {flow.description && (
                                        <p className="text-xs text-gray-500 mb-2">{flow.description}</p>
                                    )}
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-gray-500">
                                            {flow.updatedAt
                                                ? new Date(flow.updatedAt).toLocaleDateString()
                                                : 'New'}
                                        </span>
                                        <div className="flex space-x-1">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleFlowStatus(flow._id, !flow.isActive);
                                                }}
                                                className="p-1 text-xs text-gray-600 hover:text-blue-600 flex items-center justify-center"
                                                title={flow.isActive ? 'Deactivate' : 'Activate'}
                                            >
                                                <PowerIcon className={`h-4 w-4 ${flow.isActive ? 'text-green-500' : 'text-gray-400'}`} />
                                            </button>
                                            {!flow.isDefault && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDefaultFlow(flow._id);
                                                    }}
                                                    className="p-1 text-xs text-gray-600 hover:text-green-600 flex items-center justify-center"
                                                    title="Set Default"
                                                >
                                                    <StarIcon className="h-4 w-4" />
                                                </button>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    duplicateFlow(flow._id);
                                                }}
                                                className="p-1 text-xs text-gray-600 hover:text-blue-600 flex items-center justify-center"
                                                title="Duplicate"
                                            >
                                                <DocumentDuplicateIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    confirmDeleteFlow(flow._id);
                                                }}
                                                className="p-1 text-xs text-gray-600 hover:text-red-600 flex items-center justify-center"
                                                title="Delete"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {flows.length === 0 && (
                                <div className="p-4 text-center text-gray-500">
                                    <p>No flows found. Create your first flow!</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-hidden flex flex-col h-full">
                    {!selectedFlow && (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <h2 className="text-2xl font-bold text-gray-700 mb-2">Welcome to Bot Flow Builder</h2>
                                <p className="text-gray-500 mb-4">
                                    Select a flow from the sidebar or create a new one to get started.
                                </p>
                                <button
                                    onClick={createNewFlow}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                >
                                    Create New Flow
                                </button>
                            </div>
                        </div>
                    )}

                    {selectedFlow && !isEditing && (
                        <div className="p-6">
                            <div className="mb-6">
                                <h1 className="text-2xl font-bold">{selectedFlow.name}</h1>
                                {selectedFlow.description && (
                                    <p className="text-gray-500 mt-1">{selectedFlow.description}</p>
                                )}
                                <div className="mt-2 flex flex-wrap gap-2">
                                    <button
                                        onClick={handleEditFlow}
                                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                        </svg>
                                        Edit Flow
                                    </button>
                                    <button
                                        onClick={() => setShowChatTester(true)}
                                        className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 flex items-center gap-2"
                                    >
                                        <PlayIcon className="h-5 w-5" />
                                        Test Flow
                                    </button>
                                    <button
                                        onClick={() => duplicateFlow(selectedFlow._id)}
                                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 flex items-center gap-2"
                                    >
                                        <DocumentDuplicateIcon className="h-5 w-5" />
                                        Duplicate
                                    </button>
                                    <button
                                        onClick={() => toggleFlowStatus(selectedFlow._id, !selectedFlow.isActive)}
                                        className={`px-4 py-2 rounded-md ${selectedFlow.isActive
                                            ? 'bg-yellow-500 hover:bg-yellow-600'
                                            : 'bg-green-500 hover:bg-green-600'
                                            } text-white flex items-center gap-2`}
                                    >
                                        <PowerIcon className="h-5 w-5" />
                                        {selectedFlow.isActive ? 'Deactivate' : 'Activate'}
                                    </button>
                                    {!selectedFlow.isDefault && (
                                        <button
                                            onClick={() => setDefaultFlow(selectedFlow._id)}
                                            className="px-4 py-2 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 flex items-center gap-2"
                                        >
                                            <StarIcon className="h-5 w-5" />
                                            Set as Default
                                        </button>
                                    )}
                                    <button
                                        onClick={() => confirmDeleteFlow(selectedFlow._id)}
                                        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 flex items-center gap-2"
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                        Delete
                                    </button>
                                </div>
                            </div>

                            <div className="border p-4 rounded-lg">
                                <h3 className="font-medium mb-2">Flow Details</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <span className="text-sm text-gray-500">Status</span>
                                        <p className="font-medium">
                                            {selectedFlow.isActive ? 'Active' : 'Inactive'}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Default</span>
                                        <p className="font-medium">
                                            {selectedFlow.isDefault ? 'Yes' : 'No'}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Created</span>
                                        <p className="font-medium">
                                            {selectedFlow.createdAt
                                                ? new Date(selectedFlow.createdAt).toLocaleDateString()
                                                : 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Last Updated</span>
                                        <p className="font-medium">
                                            {selectedFlow.updatedAt
                                                ? new Date(selectedFlow.updatedAt).toLocaleDateString()
                                                : 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 border p-4 rounded-lg">
                                <h3 className="font-medium mb-2">Flow Preview</h3>
                                <div className="h-96 border rounded-lg bg-gray-50">
                                    {/* Read-only preview of the flow */}
                                    <BotFlowCanvas
                                        flowData={selectedFlow}
                                        readOnly={true}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedFlow && isEditing && (
                        <div className="flex-1 flex flex-col">
                            <div className="p-4 bg-gray-50 border-b">
                                <div className="flex gap-4 items-center">
                                    <div className="flex-1">
                                        <input
                                            value={selectedFlow.name}
                                            onChange={(e) =>
                                                setSelectedFlow({ ...selectedFlow, name: e.target.value })
                                            }
                                            className="w-full text-xl font-bold p-2 border rounded-md"
                                            placeholder="Flow Name"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <input
                                            value={selectedFlow.description || ''}
                                            onChange={(e) =>
                                                setSelectedFlow({ ...selectedFlow, description: e.target.value })
                                            }
                                            className="w-full p-2 border rounded-md"
                                            placeholder="Flow Description (optional)"
                                        />
                                    </div>
                                    <button
                                        onClick={() => setIsEditing(false)}
                                        className="px-3 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                            <div className="flex-1">
                                <BotFlowCanvas
                                    flowData={selectedFlow}
                                    onSave={handleSaveFlow}
                                    onTest={handleTestFlow}
                                />
                            </div>
                        </div>
                    )}

                    {/* Chat Tester Modal */}
                    <ChatFlowTester
                        isOpen={showChatTester}
                        onClose={() => setShowChatTester(false)}
                        flowData={selectedFlow}
                    />

                    {/* Delete Confirmation Modal */}
                    {showDeleteModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white p-6 rounded-lg w-96">
                                <h3 className="text-lg font-bold mb-4">Delete Flow</h3>
                                <p className="mb-4 text-sm text-gray-600">
                                    Are you sure you want to delete this flow? This action cannot be undone.
                                </p>
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setFlowToDelete(null);
                                        }}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => deleteFlow(flowToDelete)}
                                        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BotFlowManagement;